<template>
  <div class="wizard-step-compact">
    <b-alert v-if="templateLoading" show variant="light" class="mb-2 py-1">
      <b-spinner small class="mr-2" />Loading line items layout…
    </b-alert>
    <b-alert v-else show :variant="templateIsCustom ? 'success' : 'info'" class="mb-2 py-1 small">
      <feather-icon icon="LayoutIcon" size="14" class="mr-50" />
      Line Items: <strong>{{ activeTemplateName }}</strong>
      <span class="text-muted">— change the column preset on the Line Items step if needed.</span>
    </b-alert>

    <!-- Category/Vertical are set on Line Items — read-only confirm here. -->
    <div class="wizard-question">
      <h5 class="wizard-question-heading">Confirm category &amp; vertical?</h5>
      <div class="category-vertical-readonly">
        <template v-if="formData.category || formData.vertical">
          <b-badge v-if="formData.category" variant="light-primary" class="mr-1">{{ formData.category }}</b-badge>
          <b-badge v-if="formData.vertical" variant="light-secondary">{{ formData.vertical }}</b-badge>
        </template>
        <span v-else class="text-danger small">Not set yet</span>
        <b-link class="small ml-1" @click="$emit('back')">{{ formData.category && formData.vertical ? 'Change' : 'Set' }} on Line Items</b-link>
      </div>
    </div>

    <div class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'essentials' ? 'true' : 'false'"
        @click="toggleSection('essentials')"
      >
        <h5 class="wizard-question-heading mb-0">What are the invoice essentials?</h5>
        <feather-icon :icon="openSection === 'essentials' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'essentials'">
        <b-form-group label="Billing Month" label-for="billing-month" class="mb-1 mt-1">
          <b-form-select id="billing-month" v-model="formData.billingMonth" :options="billingMonthOptions" size="sm" />
        </b-form-group>
        <b-form-group label="Invoice # *" label-for="invoice-id" class="mb-1">
          <div class="d-flex align-items-center">
            <b-form-input
              id="invoice-id"
              :value="formData.invoiceId"
              class="text-uppercase"
              placeholder="Auto-fills from Vertical, or type your own"
              size="sm"
              trim
              @input="onInvoiceIdInput"
            />
            <b-button
              aria-label="Regenerate next number"
              variant="outline-secondary"
              size="sm"
              class="ml-1 flex-shrink-0"
              :disabled="invoiceNumberLoading"
              @click="$emit('regenerate-invoice-number')"
            >
              <b-spinner v-if="invoiceNumberLoading" small />
              <feather-icon v-else icon="RefreshCwIcon" size="14" />
            </b-button>
          </div>
          <small class="wizard-question-hint">Auto-fills from Vertical (SH / SHI) — always editable.</small>
          <small v-if="invoiceNumberIsFallback" class="text-warning d-block">
            Generated from existing invoices (server counter unavailable). Verify before submit.
          </small>
          <small v-if="errors.invoiceId" class="text-danger d-block">{{ errors.invoiceId }}</small>
        </b-form-group>
        <b-form-group label="Invoice Name" label-for="invoice-name" class="mb-1">
          <b-form-input
            id="invoice-name"
            :value="formData.invoiceName"
            size="sm"
            readonly
            plaintext
            class="font-weight-medium"
            placeholder="Auto from client, doc type, invoice #, billing month"
          />
          <small class="wizard-question-hint">
            Auto: Client · Doc Type · Invoice # · (Mon-YYYY)
          </small>
        </b-form-group>
        <b-form-group label="Date *" label-for="invoice-date" class="mb-1">
          <flat-pickr id="invoice-date" v-model="formData.invoiceDate" class="form-control form-control-sm" :config="{ enableTime: false, dateFormat: 'Y-m-d' }" placeholder="Date" />
          <small v-if="errors.invoiceDate" class="text-danger">{{ errors.invoiceDate }}</small>
        </b-form-group>
        <!-- GST Type is chosen on Line Items (totals). If already set, show
             read-only confirm here — do not re-offer the same selector. -->
        <div v-if="formData.gstType" class="mb-1 gst-type-already-selected">
          <label class="d-block small mb-25">GST Type *</label>
          <div class="d-flex align-items-center flex-wrap">
            <b-badge variant="light-primary" class="mr-1">{{ formData.gstType }}</b-badge>
            <b-link class="small" @click="$emit('back')">Change on Line Items</b-link>
          </div>
        </div>
        <b-form-group v-else label="GST Type *" label-for="gst-type" class="mb-1">
          <v-select id="gst-type" v-model="formData.gstType" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" label="title" :options="gstTypeOptions" placeholder="GST" :reduce="o => o.title" />
          <small v-if="errors.gstType" class="text-danger">{{ errors.gstType }}</small>
        </b-form-group>
        <b-form-group label="PO / Ref Number #" label-for="po-number" class="mb-1">
          <b-form-input id="po-number" v-model="formData.poNumber" placeholder="Auto-fills from client, or optional" size="sm" />
        </b-form-group>
        <b-form-group label="Payment Terms" label-for="payment-terms" class="mb-1">
          <v-select
            id="payment-terms"
            v-model="formData.paymentTerms"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="name"
            :options="paymentTermsOptions"
            taggable
            push-tags
            :create-option="createPaymentTermsOption"
            :placeholder="paymentTermsOptions.length ? 'Select or type payment terms' : 'Type payment terms'"
            @input="$emit('terms-selected', formData.paymentTerms)"
          >
            <template #no-options="{ search }">
              <span v-if="search">Use "{{ search }}"</span>
              <span v-else>Type custom terms — or pick from the list when available.</span>
            </template>
          </v-select>
          <small v-if="!paymentTermsOptions.length" class="wizard-question-hint">Type a custom value — set Due Date manually if needed.</small>
          <small v-else-if="formData.paymentTerms" class="wizard-question-hint">Prefills from client credit period — editable if needed.</small>
          <small v-else class="wizard-question-hint">Select from the list or type a custom value.</small>
        </b-form-group>
        <b-form-group label="Due Date" label-for="due-date" class="mb-1">
          <flat-pickr id="due-date" v-model="formData.dueDate" class="form-control form-control-sm" :config="{ enableTime: false, dateFormat: 'Y-m-d' }" placeholder="Due date" />
        </b-form-group>
        <b-form-group label="Description" label-for="description" label-class="wizard-description-label" class="mb-1">
          <b-form-input id="description" v-model="formData.description" placeholder="Optional note (shown bold on the invoice)" size="sm" />
        </b-form-group>
        <div class="msme-terms-prompt">
          <b-form-checkbox v-model="formData.includeMsmeTerms" switch class="mb-50">
            Include MSME note (Notes) on this invoice?
          </b-form-checkbox>
          <small class="wizard-question-hint d-block">
            When enabled, the invoice shows: (1) MSME registration UDYAM-MH-26-0202228;
            (2) max 45-day credit period with 18% p.a. interest thereafter per MSME Act.
          </small>
        </div>
      </b-collapse>
    </div>

    <div class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'codes' ? 'true' : 'false'"
        @click="toggleSection('codes')"
      >
        <h5 class="wizard-question-heading mb-0">Any reference codes?</h5>
        <feather-icon :icon="openSection === 'codes' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'codes'">
        <b-form-checkbox v-model="formData.hasPackageCode" switch class="mb-1 mt-1">
          Has Package Code?
        </b-form-checkbox>
        <b-collapse :visible="!!formData.hasPackageCode" class="mb-1">
          <b-form-group label="Package Code" label-for="package-code" class="mb-0">
            <b-form-input id="package-code" v-model="formData.packageCode" placeholder="e.g. PKG-1, GJV-96" size="sm" trim />
          </b-form-group>
        </b-collapse>
        <b-form-group label="SAC / HSN Code" label-for="sac-hsn-code-header" label-class="small mb-0" class="mb-1">
          <v-select
            id="sac-hsn-code-header"
            :value="formData.invoiceSattvaInfo.sacCode"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="title"
            :options="sacCodeSelectOptions"
            taggable
            push-tags
            :create-option="createSacHsnOption"
            :reduce="o => (typeof o === 'string' ? o : o.value)"
            placeholder="Select or type a SAC / HSN Code"
            @input="onSacHsnChanged"
          >
            <template #no-options="{ search }">
              <span v-if="search">Use "{{ search }}"</span>
              <span v-else>Type a code — no SAC/HSN options loaded yet.</span>
            </template>
          </v-select>
          <small v-if="!sacCodeSelectOptions.length" class="wizard-question-hint">Not set up yet — type a new code for this invoice.</small>
          <small v-else class="wizard-question-hint">Default for this invoice — line items can still override.</small>
        </b-form-group>
        <b-form-group label="LUT No." label-for="lut-no-header" label-class="small mb-0" class="mb-0">
          <v-select
            id="lut-no-header"
            :value="formData.invoiceSattvaInfo.lutNo"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="title"
            :options="lutNoOptions"
            taggable
            push-tags
            :create-option="createLutNoOption"
            :reduce="o => (typeof o === 'string' ? o : o.value)"
            placeholder="Select or type a LUT number"
            @input="onLutNoChanged"
          >
            <template #no-options="{ search }">
              <span v-if="search">Use "{{ search }}"</span>
              <span v-else>Type a LUT number — none loaded from location yet.</span>
            </template>
          </v-select>
        </b-form-group>
      </b-collapse>
    </div>

    <div v-if="extraHeaderFields.length > 0" class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'extra' ? 'true' : 'false'"
        @click="toggleSection('extra')"
      >
        <h5 class="wizard-question-heading mb-0">Any additional fields from the template?</h5>
        <feather-icon :icon="openSection === 'extra' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'extra'">
        <small class="wizard-question-hint mb-1 d-block mt-1">From line items layout: {{ activeTemplate.name }}</small>
        <div v-for="field in extraHeaderFields" :key="field.key" class="mb-1">
          <b-form-group :label="field.label + (field.required ? ' *' : '')" :label-for="'extra-field-' + field.key" label-class="small mb-0">
            <flat-pickr v-if="field.type === 'date'" :id="'extra-field-' + field.key" v-model="extraFields[field.key]" class="form-control form-control-sm" :config="{ enableTime: false, dateFormat: 'Y-m-d' }" />
            <b-form-textarea v-else-if="field.type === 'textarea'" :id="'extra-field-' + field.key" v-model="extraFields[field.key]" rows="2" size="sm" />
            <v-select v-else-if="field.type === 'select'" :id="'extra-field-' + field.key" v-model="extraFields[field.key]" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="field.options || []" />
            <b-form-input v-else-if="field.type === 'number'" :id="'extra-field-' + field.key" v-model.number="extraFields[field.key]" type="number" size="sm" />
            <b-form-input v-else :id="'extra-field-' + field.key" v-model="extraFields[field.key]" size="sm" />
          </b-form-group>
        </div>
      </b-collapse>
    </div>

    <div class="wizard-nav-actions d-flex justify-content-between align-items-center w-100 flex-wrap">
      <b-button
        variant="outline-secondary"
        size="sm"
        class="wizard-icon-btn"
        aria-label="Back"
        @click="$emit('back')"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" />
      </b-button>
      <b-button
        variant="primary"
        size="sm"
        class="wizard-icon-btn"
        aria-label="Next: Payment"
        @click="onNext"
      >
        <feather-icon icon="ArrowRightIcon" size="16" />
      </b-button>
      <small v-if="!isCategoryVerticalValid" class="text-danger d-block mt-1 w-100">
        Category/Vertical not set —
        <b-link class="text-danger" style="text-decoration: underline;" @click="$emit('back')">go back to Line Items</b-link>.
      </small>
      <small v-else-if="!isValid" class="text-muted d-block mt-1 w-100">Fill in the required fields above (marked *) to continue.</small>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup, BFormInput, BFormTextarea, BFormSelect,
  BButton, BAlert, BSpinner, BCollapse,
  BFormCheckbox, BBadge, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'

const CORE_HEADER_KEYS = new Set([
  'invoiceDate', 'invoiceId', 'paymentTerms', 'dueDate',
  'poNumber', 'gstType', 'description', 'billingMonth',
])

export default {
  name: 'WizardMoreDetailsStep',
  components: {
    BFormGroup, BFormInput, BFormTextarea, BFormSelect,
    BButton, BAlert, BSpinner, BCollapse,
    BFormCheckbox, BBadge, BLink, vSelect, flatPickr,
  },
  props: {
    formData: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    billingMonthOptions: { type: Array, default: () => [] },
    activeTemplateName: { type: String, default: '' },
    templateIsCustom: { type: Boolean, default: false },
    templateLoading: { type: Boolean, default: false },
    paymentTermsOptions: { type: Array, default: () => [] },
    gstTypeOptions: { type: Array, default: () => [] },
    activeTemplate: { type: Object, default: () => ({}) },
    invoiceNumberLoading: { type: Boolean, default: false },
    invoiceNumberIsFallback: { type: Boolean, default: false },
    sacCodeSelectOptions: { type: Array, default: () => [] },
    lutNoOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      extraFields: {},
      // Progressive disclosure: one More Details section open at a time.
      openSection: 'essentials',
    }
  },
  computed: {
    extraHeaderFields() {
      const fields = (this.activeTemplate && this.activeTemplate.headerFields) || []
      return fields.filter((f) => !CORE_HEADER_KEYS.has(f.key) && f.visible !== false)
    },
    isCategoryVerticalValid() {
      return !!(this.formData.category && this.formData.vertical)
    },
    isDetailsValid() {
      const coreOk = !!(this.formData.invoiceId && this.formData.invoiceDate && this.formData.gstType)
      if (!coreOk) return false
      if (this.formData.hasPackageCode && !String(this.formData.packageCode || '').trim()) return false
      return this.extraHeaderFields
        .filter((f) => f.required)
        .every((f) => !!this.extraFields[f.key])
    },
    isValid() {
      return this.isCategoryVerticalValid && this.isDetailsValid
    },
  },
  watch: {
    extraFields: {
      deep: true,
      handler(vals) {
        Object.keys(vals).forEach((k) => {
          this.$set(this.formData, k, vals[k])
        })
      },
    },
    activeTemplate: {
      immediate: true,
      handler() {
        const fields = (this.activeTemplate && this.activeTemplate.headerFields) || []
        fields.forEach((f) => {
          if (!CORE_HEADER_KEYS.has(f.key) && f.visible !== false) {
            if (this.extraFields[f.key] === undefined) {
              this.$set(this.extraFields, f.key, f.default ?? (f.type === 'number' ? 0 : ''))
            }
          }
        })
      },
    },
    'formData.hasPackageCode'(val) {
      if (!val) this.formData.packageCode = ''
    },
  },
  methods: {
    toggleSection(key) {
      this.openSection = this.openSection === key ? '' : key
    },
    onInvoiceIdInput(val) {
      this.formData.invoiceId = val
      this.$emit('invoice-id-edited', val)
    },
    // Creatable select: typed codes become {title,value} for push-tags.
    createSacHsnOption(search) {
      const code = String(search || '').trim()
      return { title: code, value: code }
    },
    createLutNoOption(search) {
      const code = String(search || '').trim()
      return { title: code, value: code }
    },
    // Free-text Payment Terms: keep { name, days } so dropdown label + due-date
    // prefill still work when the typed text includes a day count (e.g. "Net 45").
    createPaymentTermsOption(search) {
      const name = String(search || '').trim()
      const match = name.match(/(\d+)/)
      const days = match ? parseInt(match[1], 10) : null
      return { name, days: Number.isNaN(days) ? null : days }
    },
    onSacHsnChanged(val) {
      const code = val != null && String(val).trim() !== '' ? String(val).trim() : ''
      this.$set(this.formData.invoiceSattvaInfo, 'sacCode', code || null)
    },
    onLutNoChanged(val) {
      const code = val != null && String(val).trim() !== '' ? String(val).trim() : ''
      this.$set(this.formData.invoiceSattvaInfo, 'lutNo', code)
    },
    onNext() {
      this.$emit('next')
    },
  },
}
</script>

<style src="./wizard-step-shared.css"></style>
<style scoped>
.category-vertical-readonly {
  min-height: 28px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.category-vertical-readonly .badge {
  font-size: 0.76rem;
  font-weight: 500;
  padding: 0.35rem 0.6rem;
}
.category-vertical-readonly a {
  font-size: 0.72rem;
}
.msme-terms-prompt {
  margin-top: 0.35rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e3e3e3;
}
</style>
